/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */

/**
 * TextEmbeddingVisualizer.tsx
 *
 * Main React component for the semantic cartographer visualization app.
 * Handles all UI state, data flow, and orchestrates the Three.js scene and overlays.
 *
 * Features:
 * - Uses ThreeSceneManager to manage Three.js scene, renderer, camera, and VR logic.
 * - Uses EmbeddingsGroup to render all 3D objects (points, lines, curve, labels).
 * - Provides UI panels for controls, chunk list, and PCA matrix.
 * - Orchestrates the backend pipeline: source text → chunk set → embed → PCA basis → projection.
 * - Supports VR mode and shareable session URLs.
 *
 * Author: T.J. McLeish
 */


import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTextEmbeddings } from '../hooks/useTextEmbeddings';
import ThreeSceneManager from './ThreeSceneManager';
import EmbeddingsGroup from './EmbeddingsGroup';
import ControlsPanel from './ControlsPanel';
import { PointStyle } from '../objects/CustomPointHelper';
import {
  DisplayParamTokenColorMode,
  DisplayParamEnvironmentColorMode,
  DisplayParamFogColorMode,
  DisplayParamLabelColorMode
} from '../constants';
import ChunkList from './ChunkList';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import ListIcon from '@mui/icons-material/List';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getTokenColor, getSequenceColor, getSourceColor } from '../utils/colorUtils';
import PCAMatrixPanel from './PCAMatrixPanel';


/**
 * @typedef {import('../types/chunk').Chunk} Chunk
 * @typedef {import('../types/token').Token} Token
 * @typedef {import('../types/embedding').Embedding} Embedding
 * @typedef {import('../types/pca').PCA} PCA
 * @typedef {import('../types/embeddingModelInfo').EmbeddingModelInfo} EmbeddingModelInfo
 * @typedef {import('../types/visualizationParams').VisualizationParams} VisualizationParams
 */


// Example using Google Fonts CDN (Roboto Regular)
const robotoFontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf';

/**
 * TextEmbeddingVisualizer
 *
 * Main visualization component. Manages all UI state, data flow, and coordinates the 3D scene.
 *
 * @component
 * @param {Object} props
 * @param {function} props.tokenizer - Tokenizer function for text input
 * @returns {JSX.Element}
 */
export default function TextEmbeddingVisualizer({ tokenizer = null, sessionId = null }: { tokenizer?: unknown; sessionId?: string | null }) {
  type PointStyleValue = typeof PointStyle[keyof typeof PointStyle];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // On mobile, panels start collapsed; on desktop they are always visible.
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  // Close panels when switching between mobile and desktop
  useEffect(() => {
    if (!isMobile) {
      setLeftPanelOpen(false);
      setRightPanelOpen(false);
    }
  }, [isMobile]);

  // Point style state for 3D points
  const [pointStyle, setPointStyle] = useState<PointStyleValue>(PointStyle.CLOUD);

  // Handler for point style changes from UI
  const handleChangePointStyle = React.useCallback((style) => {
    console.debug('[TextEmbeddingVisualizer] handleChangePointStyle:', style);
    setPointStyle(style);
  }, []);
  // Ref to the canvas element for Three.js rendering
  const canvasRef = useRef(null);
  
  // Ref to the ThreeSceneManager for accessing scene/camera/renderer/VR handler
  const sceneManagerRef = useRef(null);

  // --------------------
  // UI and Visualization State
  // --------------------
  // Main UI state variables
  // Display parameter color modes
  const [displayParam_tokenColorMode, setDisplayParam_tokenColorMode] = useState<string>(DisplayParamTokenColorMode.SEQUENCE);
  const [displayParam_environmentColorMode, setDisplayParam_environmentColorMode] = useState<string>(DisplayParamEnvironmentColorMode.DARK_BLUE);
  const [displayParam_fogColorMode, setDisplayParam_fogColorMode] = useState<string>(DisplayParamFogColorMode.DARK_BLUE);
  // Label coloring mode for display parameters panel
  const [displayParam_labelColorMode, setDisplayParam_labelColorMode] = useState<string>(DisplayParamLabelColorMode.SEQUENCE);
  const [embeddingDimensions, setEmbeddingDimensions] = useState(3072); // Embedding dimension
  const [showLabels, setShowLabels] = useState(true); // Show first-word labels
  const [textSize, setTextSize] = useState(2); // Text label size
  const [dotSize, setDotSize] = useState(2);   // Point size
  const [labelOffset, setLabelOffset] = useState(0.0); // Vertical offset for labels
  const [highlightedIndex, setHighlightedIndex] = useState(0); // Highlighted label index
  const [showPathMode, setShowPathMode] = useState('curve'); // Path display mode
  const [showHelpers, setShowHelpers] = useState(true); // Show 3D helpers (axes/grid)
  const [showPCAMatrixPanel, setShowPCAMatrixPanel] = useState(false); // Show PCA matrix panel
  const [selectedBasisId, setSelectedBasisId] = useState<string | null>(null); // Selected PCA basis for backend projection
  const [activeChunkSetIds, setActiveChunkSetIds] = useState<string[]>([]); // Active chunk set IDs for multi-corpus
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(sessionId || null);

  // Compute projected chunks and PCA basis metadata using backend APIs.
  const { enrichedChunks, isLoading, processChunks, pcaResult } = useTextEmbeddings({
    embeddingDimensions,
  });


  // VR handler (delegated to ThreeSceneManager)
  const handleEnterVR = useCallback(() => {
    if (sceneManagerRef.current && sceneManagerRef.current.handleEnterVR) {
      sceneManagerRef.current.handleEnterVR();
    }
  }, []);

  const applySessionDisplayParams = useCallback((displayParams: Record<string, unknown> = {}) => {
    const colorMode = String(displayParams.colorMode || '');
    const labelColorMode = String(displayParams.labelColorMode || '');
    const environmentColorMode = String(displayParams.environmentColorMode || '');
    const fogColorMode = String(displayParams.fogColorMode || '');
    if (
      colorMode &&
      (Object.values(DisplayParamTokenColorMode) as string[]).includes(colorMode)
    ) {
      setDisplayParam_tokenColorMode(colorMode);
    }
    if (
      labelColorMode &&
      (Object.values(DisplayParamLabelColorMode) as string[]).includes(labelColorMode)
    ) {
      setDisplayParam_labelColorMode(labelColorMode);
    }
    if (
      environmentColorMode &&
      (Object.values(DisplayParamEnvironmentColorMode) as string[]).includes(environmentColorMode)
    ) {
      setDisplayParam_environmentColorMode(environmentColorMode);
    }
    if (
      fogColorMode &&
      (Object.values(DisplayParamFogColorMode) as string[]).includes(fogColorMode)
    ) {
      setDisplayParam_fogColorMode(fogColorMode);
    }
    if (displayParams.showLabels != null) setShowLabels(Boolean(displayParams.showLabels));
    if (displayParams.textSize != null) setTextSize(Number(displayParams.textSize));
    if (displayParams.dotSize != null) setDotSize(Number(displayParams.dotSize));
    if (displayParams.labelOffset != null) setLabelOffset(Number(displayParams.labelOffset));
    if (displayParams.showPathMode) setShowPathMode(String(displayParams.showPathMode));
    const allowedPointStyles = [PointStyle.CIRCLE, PointStyle.RING, PointStyle.CLOUD];
    const pointStyle = String(displayParams.pointStyle || '');
    if (pointStyle && allowedPointStyles.includes(pointStyle as PointStyleValue)) {
      setPointStyle(pointStyle as PointStyleValue);
    }
    if (displayParams.showHelpers != null) setShowHelpers(Boolean(displayParams.showHelpers));
  }, []);

  const loadSessionVisualization = useCallback(async (
    nextSessionId: string,
    options: { updateHash?: boolean } = {}
  ) => {
    const { updateHash = true } = options;
    try {
      const res = await fetch(`/api/visualization-sessions/${encodeURIComponent(nextSessionId)}`);
      if (!res.ok) {
        throw new Error(`GET /api/visualization-sessions/${nextSessionId} failed (${res.status})`);
      }
      const session = await res.json();
      const sessionChunkSets = Array.isArray(session.session_chunk_sets)
        ? session.session_chunk_sets.filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
        : [];
      const pcaBasisId = typeof session.pca_basis_id === 'string' ? session.pca_basis_id : null;

      setSelectedSessionId(nextSessionId);
      setSelectedBasisId(pcaBasisId);
      setActiveChunkSetIds(sessionChunkSets);
      applySessionDisplayParams(session.display_params || {});

      if (updateHash) {
        const nextHash = `#/session/${nextSessionId}`;
        if (window.location.hash !== nextHash) {
          window.location.hash = nextHash;
        }
      }

      if (!pcaBasisId || sessionChunkSets.length === 0) {
        console.warn('[TextEmbeddingVisualizer] Session has no renderable chunk set/PCA basis pair.');
        return;
      }

      await processChunks({ chunkSetIds: sessionChunkSets, pcaBasisId });
    } catch (err) {
      console.error('[TextEmbeddingVisualizer] Failed to load session visualization:', err);
      alert('Failed to load visualization session. See console for details.');
    }
  }, [applySessionDisplayParams, processChunks]);


  // (Optional) useEffect for automatic label highlighting, if desired
  // Automatically highlight labels every second
  // useEffect(() => {
  //   console.log('[TextEmbeddingVisualizer][useEffect] Automatic label highlighting effect triggered:', { enrichedChunksLength: enrichedChunks.length });
  //   if (!enrichedChunks.length) return;
  //   const interval = setInterval(() => {
  //     setHighlightedIndex(idx => (idx + 1) % enrichedChunks.length);
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [enrichedChunks.length]);



  // Load session state and render projected chunks when the route contains a session id.
  useEffect(() => {
    if (!sessionId) {
      setSelectedSessionId(null);
      return;
    }
    if (sessionId === selectedSessionId) {
      return;
    }
    void loadSessionVisualization(sessionId, { updateHash: false });
  }, [loadSessionVisualization, selectedSessionId, sessionId]);

  /**
   * Save the current visualization as a backend session and navigate to its shareable URL.
   */
  const handleExportSession = useCallback(async () => {
    const sessionName = `Session ${new Date().toLocaleString()}`;
    try {
      const res = await fetch('/api/visualization-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: sessionName,
          pca_basis_id: selectedBasisId || undefined,
          session_chunk_sets: activeChunkSetIds,
          display_params: {
            colorMode: displayParam_tokenColorMode,
            labelColorMode: displayParam_labelColorMode,
            environmentColorMode: displayParam_environmentColorMode,
            fogColorMode: displayParam_fogColorMode,
            showLabels,
            textSize,
            dotSize,
            labelOffset,
            showPathMode,
            pointStyle,
            showHelpers,
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { id } = await res.json();
      window.location.hash = `#/session/${id}`;
    } catch (err) {
      console.error('[TextEmbeddingVisualizer][handleExportSession] Error:', err);
      alert('Failed to save session. See console for details.');
    }
  }, [
    selectedBasisId, activeChunkSetIds,
    displayParam_tokenColorMode, displayParam_labelColorMode,
    displayParam_environmentColorMode, displayParam_fogColorMode,
    showLabels, textSize, dotSize, labelOffset, showPathMode, pointStyle, showHelpers,
  ]);

  // --------------------
  // Render
  // --------------------
  // Main render: layout the 3D scene, overlays, and control panels
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f8fafc',
      }}
    >
      {/* 3D Canvas and Three.js scene */}
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          zIndex: 0,
        }}
      />
      {/* Three.js scene manager and embeddings group */}
      <ThreeSceneManager ref={sceneManagerRef} canvasRef={canvasRef} showHelpers={showHelpers}>
        <EmbeddingsGroup
          sceneRef={sceneManagerRef.current ? sceneManagerRef.current.sceneRef : { current: null }}
          threeSceneManagerRef={sceneManagerRef}
          enrichedChunks={enrichedChunks}
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          displayParam_fogColorMode={displayParam_fogColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          showLabels={showLabels}
          textSize={textSize}
          dotSize={dotSize}
          labelOffset={labelOffset}
          showPathMode={showPathMode}
          getTokenColor={getTokenColor}
          getSequenceColor={getSequenceColor}
          getSourceColor={getSourceColor}
          pointStyle={pointStyle}
        />
      </ThreeSceneManager>

      {/* Left panel: ControlsPanel
          On desktop: always visible at top-left.
          On mobile: slides in from the left when leftPanelOpen, hidden off-screen otherwise. */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? 0 : 24,
          left: 0,
          zIndex: 10,
          pointerEvents: 'auto',
          maxWidth: isMobile ? '85vw' : 400,
          minWidth: isMobile ? 0 : 320,
          width: isMobile ? 'min(340px, 85vw)' : undefined,
          height: isMobile ? '100vh' : undefined,
          transform: isMobile
            ? leftPanelOpen ? 'translateX(0)' : 'translateX(-110%)'
            : 'translateX(24px)',
          transition: isMobile ? 'transform 0.3s cubic-bezier(0.4,0,0.2,1)' : 'none',
          overflow: isMobile ? 'auto' : 'visible',
        }}
      >
        <ControlsPanel
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          onChangeDisplayParam_tokenColorMode={setDisplayParam_tokenColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          onChangeDisplayParam_environmentColorMode={setDisplayParam_environmentColorMode}
          displayParam_fogColorMode={displayParam_fogColorMode}
          onChangeDisplayParam_fogColorMode={setDisplayParam_fogColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          onChangeDisplayParam_labelColorMode={setDisplayParam_labelColorMode}
          embeddingDimensions={embeddingDimensions}
          onEmbeddingDimensionsChange={setEmbeddingDimensions}
          isLoading={isLoading}
          showLabels={showLabels}
          onToggleShowLabels={setShowLabels}
          textSize={textSize}
          onTextSizeChange={setTextSize}
          dotSize={dotSize}
          onDotSizeChange={setDotSize}
          labelOffset={labelOffset}
          onLabelOffsetChange={setLabelOffset}
          showPathMode={showPathMode}
          onChangeShowPathMode={setShowPathMode}
          showHelpers={showHelpers}
          onToggleShowHelpers={setShowHelpers}
          showPCAMatrix={showPCAMatrixPanel}
          onToggleShowPCAMatrix={setShowPCAMatrixPanel}
          selectedSessionId={selectedSessionId}
          onSelectSession={loadSessionVisualization}
          getTokenColor={getTokenColor}
          getSequenceColor={getSequenceColor}
          handleExportSession={handleExportSession}
          onEnterVR={handleEnterVR}
          onChangePointStyle={handleChangePointStyle}
          pointStyle={pointStyle}
        />
      </Box>

      {/* Right panel: ChunkList
          On desktop: always visible at top-right.
          On mobile: slides in from the right when rightPanelOpen, hidden off-screen otherwise. */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? 0 : 24,
          right: 0,
          zIndex: 10,
          pointerEvents: 'auto',
          maxWidth: isMobile ? '85vw' : 400,
          minWidth: isMobile ? 0 : 320,
          width: isMobile ? 'min(340px, 85vw)' : undefined,
          height: isMobile ? '100vh' : 'calc(100vh - 100px)',
          transform: isMobile
            ? rightPanelOpen ? 'translateX(0)' : 'translateX(110%)'
            : 'translateX(-24px)',
          transition: isMobile ? 'transform 0.3s cubic-bezier(0.4,0,0.2,1)' : 'none',
          overflow: 'hidden',
        }}
      >
        {enrichedChunks.length > 0 && (
          <ChunkList
            chunks={enrichedChunks}
            chunkList_tokenColorMode={displayParam_tokenColorMode}
            getTokenColor={getTokenColor}
            getSequenceColor={getSequenceColor}
          />
        )}
      </Box>

      {/* Mobile-only backdrop: tapping outside an open panel closes it */}
      {isMobile && (leftPanelOpen || rightPanelOpen) && (
        <Box
          onClick={() => { setLeftPanelOpen(false); setRightPanelOpen(false); }}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 9,
            backgroundColor: 'rgba(0,0,0,0.35)',
          }}
        />
      )}

      {/* Mobile-only toggle buttons: left panel (☰) and right panel (list) */}
      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Tooltip title="Open controls" placement="right">
            <IconButton
              size="small"
              onClick={() => { setLeftPanelOpen((v) => !v); setRightPanelOpen(false); }}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(4px)',
                boxShadow: 2,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
              }}
              aria-label={leftPanelOpen ? 'Close controls' : 'Open controls'}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {isMobile && enrichedChunks.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 20,
          }}
        >
          <Tooltip title="Open chunk list" placement="left">
            <IconButton
              size="small"
              onClick={() => { setRightPanelOpen((v) => !v); setLeftPanelOpen(false); }}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(4px)',
                boxShadow: 2,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
              }}
              aria-label={rightPanelOpen ? 'Close chunk list' : 'Open chunk list'}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* PCA matrix panel (bottom) */}
      {pcaResult && pcaResult.pcaMatrix && pcaResult.pcaMatrix.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 24,
            right: 24,
            bottom: 24,
            zIndex: 20,
            pointerEvents: 'auto',
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
            boxShadow: 3,
            p: 0,
          }}
        >
          <PCAMatrixPanel pcaResult={pcaResult} cellHeight={8} />
        </Box>
      )}
    </Box>
  );
}
